import { clsx } from "clsx";
import { JSX } from "react";
import { spinnerOverlay } from "../button/Button.css";
import type { IconProps } from "../icon/Icon";
import { Icon } from "../icon/Icon";
import { ProgressSpinner } from "../progressSpinner/ProgressSpinner";
import {
  iconButtonBase,
  iconButtonSizes,
  iconButtonVariants,
  iconWrapper
} from "./IconButton.css";

export interface IconButtonPropsCustom {
  /**
   * The icon to be displayed in the button.
   * This should be a valid icon name that the Icon component can render.
   */
  iconName: IconProps["name"];
  /**
   * The size of the button.
   * This affects the button's font size and height.
   * Options include: "md", "sm", "lg".
   */
  size?: keyof typeof iconButtonSizes;
  /**
   * The variant of the button.
   * This determines the button's appearance and behavior.
   * Options include: "primary", "secondary", "tertiary".
   */
  variant?: keyof typeof iconButtonVariants;
  /**
   * Whether the button is disabled.
   */
  disabled?: boolean;
  /**
   * Whether the button is in a loading state.
   */
  isLoading?: boolean;

  /**
   * Additional class name(s) to apply to the button for custom styling.
   */
  className?: string;
}

export interface IconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    IconButtonPropsCustom {}

export function IconButton({
  variant = "primary",
  size = "md",
  iconName,
  disabled,
  isLoading,
  className,
  ...props
}: IconButtonProps): JSX.Element {
  return (
    <button
      className={clsx(
        iconButtonBase,
        iconButtonVariants[variant],
        iconButtonSizes[size],
        className
      )}
      disabled={disabled}
      {...props}
      aria-busy={isLoading}
    >
      {isLoading ? (
        <span className={spinnerOverlay}>
          <ProgressSpinner />
        </span>
      ) : (
        <span className={iconWrapper} aria-hidden="true">
          <Icon name={iconName} />
        </span>
      )}
    </button>
  );
}
